import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { BackHeader } from '@/components/BackHeader/BackHeader'
import { LoadingReport } from '@/components/LoadingReport/LoadingReport'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useGeneratingReportGuard } from '@/hooks/useGeneratingReportGuard'
import { useLazyGetReportStatusQuery } from '@/store/api/NewSearch/NewSearch.query'
import { EReportStatus } from '@/store/api/NewSearch/NewSearch.types'

import {
  analyzingMessages,
  genericLoadingMessages,
  scrapingSocialMediaMessages,
} from '../Setup/Const/LoadingMessages'

export const GeneratingReport = () => {
  const { t } = useTranslation()

  // Guard para proteger la ruta
  const { isValidating, isAuthorized } = useGeneratingReportGuard()

  const [reportId] = useState(() => localStorage.getItem('reportId'))

  const [progress, setProgress] = useState(0)

  const [loadingMessage, setLoadingMessage] = useState(
    t('search.generatingReport.loading.searching.subject'),
  )

  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    scrapingSocialMediaMessagesIndex,
    setScrapingSocialMediaMessagesIndex,
  ] = useState(0)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const [getReportStatus, { data: reportStatus }] =
    useLazyGetReportStatusQuery()

  const startInterval = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (subjectId: string, id: string) => {
      const checkReportStatus = async () => {
        try {
          const response = await getReportStatus({ subjectId }).unwrap()
          handleLoadingMessage(
            response.info.status,
            response.info.currenScraping,
          )
          if (
            response.info.status === EReportStatus.Completed ||
            response.info.progress === 100
          ) {
            stopInterval()
            setLoadingMessage(
              t('search.generatingReport.loading.generatingReport'),
            )

            localStorage.removeItem('subjectId')
            localStorage.removeItem('reportId')

            setTimeout(() => {
              navigate(AppRoutes.digitalReport, {
                state: { id: reportId, subjectId },
              })
            }, 6000)
          }
        } catch (e) {
          console.error('Error al obtener estado del reporte', e)
          stopInterval()
          catchWithSentry(e)
          toast.error(t('feedback.toast.errorFetchingStatus'))
        }
      }

      stopInterval()

      intervalRef.current = setInterval(checkReportStatus, 5000)
    },
    [getReportStatus, navigate, t, dispatch],
  )

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!isAuthorized || isValidating) {
      return
    }

    const storedSubjectId = localStorage.getItem('subjectId')
    const storedReportId = localStorage.getItem('reportId')

    if (storedSubjectId && storedReportId) {
      startInterval(storedSubjectId, storedReportId)
    }

    return () => {
      stopInterval()
    }
  }, [isAuthorized, isValidating, startInterval, stopInterval])

  useEffect(() => {
    if (reportStatus?.info.progress) {
      setProgress(reportStatus.info.progress)
    }
  }, [reportStatus])

  const formatSocialMedia = (platform: string) => {
    switch (platform) {
      case 'LinkedIn':
        return 'LinkedIn'
      case 'Facebook':
        return 'Facebook'
      case 'Instagram':
        return 'Instagram'
      case 'Pinterest':
        return 'Pinterest'
      case 'TikTok':
        return 'TikTok'
      case 'Twitter':
        return 'X (Twitter)'
      case 'Reddit':
        return 'Reddit'
      case 'WebSearch':
        return 'Web/News'
      default:
        return 'various platforms'
    }
  }

  const getScrapingMessage = (newIndex: number, socialMedia: string) => {
    const messageTemplate = t(scrapingSocialMediaMessages[newIndex])
    const formattedMessage = messageTemplate.replace(
      '${socialMedia}',
      formatSocialMedia(socialMedia),
    )
    return formattedMessage
  }

  const handleLoadingMessage = (status: string, currenScraping: string) => {
    if (status === null || currenScraping === null) {
      setLoadingMessage(t('search.generatingReport.loading.generic.searching'))
    } else if (status === 'Scraping' && currenScraping !== null) {
      setScrapingSocialMediaMessagesIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % scrapingSocialMediaMessages.length
        setLoadingMessage(getScrapingMessage(newIndex, currenScraping))
        return newIndex
      })
    } else if (status === 'Searching' && currenScraping !== null) {
      setLoadingMessage((prevMessage) => {
        const newIndex =
          (genericLoadingMessages.indexOf(prevMessage) + 1) %
          genericLoadingMessages.length
        return t(genericLoadingMessages[newIndex])
      })
    } else if (status === 'IaAnalysis') {
      setLoadingMessage((prevMessage) => {
        const newIndex =
          (analyzingMessages.indexOf(prevMessage) + 1) %
          analyzingMessages.length
        return t(analyzingMessages[newIndex])
      })
    } else if (status === 'Completed') {
      setLoadingMessage(t('search.generatingReport.loading.generatingReport'))
    } else {
      setLoadingMessage(t('search.generatingReport.loading.processingData'))
    }
  }

  const onNavigateBack = () => {
    navigate(AppRoutes.dashboard)
  }

  // No renderizar nada mientras se está validando o si no está autorizado
  // El guard se encarga de redirigir si no está autorizado
  if (isValidating || !isAuthorized) {
    return null
  }

  return (
    <WeScreen useHeader={false}>
      <div className="absolute z-50 top-10 left-10">
        <BackHeader onPressBack={onNavigateBack} />
      </div>
      <LoadingReport
        progress={progress}
        title={t('search.generatingReport.title')}
        description={t('search.generatingReport.footerText')}
        progressText={loadingMessage}
      />
    </WeScreen>
  )
}
